/**
 * Quản trị "Mục đối chiếu" — bật/tắt từng luật nông học A/B và sửa nội dung cảnh báo.
 *
 * Từ 2026-08-08 các luật này không chặn nộp phiếu nữa: chúng là số liệu bất thường
 * để người duyệt xem lại. Kèm theo đó là nhu cầu tắt bớt — một luật báo sai liên tục
 * trên vùng canh tác cụ thể sẽ làm điều tra viên quen với màu đỏ và bỏ qua cả những
 * cảnh báo thật.
 *
 * Sửa được cả tiêu đề và dòng "Nên làm" vì text mặc định viết cho người đọc chung,
 * còn mỗi đơn vị điều tra lại có cách diễn đạt và quy trình hỏi lại nông hộ riêng.
 * Hai dòng "Hiện tại"/"Khuyến nghị" KHÔNG sửa ở đây: chúng là số dựng từ dữ liệu phiếu
 * và bảng ngưỡng, muốn đổi thì đổi ngưỡng ở tab "Ngưỡng đối chiếu".
 *
 * Thiết lập này áp dụng NGAY cho mọi lần chạy audit kế tiếp, không snapshot theo
 * phiên bản bộ câu hỏi (xem AuditRuleSettingsService phía API để biết vì sao).
 * Các `audit_findings` đã ghi ở những phiếu nộp trước đó giữ nguyên — tắt một luật
 * hay đổi text không viết lại lịch sử, chỉ đổi cảnh báo sinh ra từ đây trở đi.
 */
import { AUDIT_RULE_DEFAULT_TEXT, DYNAMIC_MARK } from '@farmer/audit-rules';
import * as api from '../api-client.js';
import { esc, toast } from '../ui/util.js';

const FORM_LABEL = { onfarm: 'Phỏng vấn nông hộ', removal: 'Đo đạc trên vườn' };

/**
 * Text mặc định, đã escape, với mỗi chỗ nội suy tô lên thành một chip.
 *
 * Bày nguyên chuỗi phẳng thì dấu `…` lẫn vào dấu ba chấm của câu văn, mà đây đúng là
 * chỗ admin cần nhìn rõ nhất: viết đè bằng text tĩnh là xoá mất phần động đó.
 */
function marked(text) {
  return esc(text)
    .split(DYNAMIC_MARK)
    .join(`<span class="ar-dyn" title="Hệ thống tự điền số liệu của phiếu vào đây">${esc(DYNAMIC_MARK)}</span>`);
}

/** Ô giữ chỗ dùng được trong text tuỳ chỉnh — khớp RULE_TEXT_PLACEHOLDERS của package. */
const PLACEHOLDER_HINT = [
  ['{title}', 'tiêu đề hệ thống sinh'],
  ['{now}', 'dòng “Hiện tại”'],
  ['{rec}', 'dòng “Khuyến nghị”'],
  ['{fix}', 'dòng “Nên làm” gốc'],
  ['{code}', 'mã luật'],
];

export async function renderAuditRules(container) {
  const user = api.getCurrentUser();
  const readonly = !user || user.role !== 'admin';

  container.innerHTML = `<div class="empty-state">Đang tải danh mục luật...</div>`;

  let rules = [];
  try {
    rules = await api.auditRules.list();
  } catch (err) {
    container.innerHTML = `<div class="empty-state"><span class="ms ms-xl">error</span>
      <p>${esc(err.message)}</p></div>`;
    return;
  }

  const snapshot = (r) => ({
    enabled: r.enabled,
    titleText: r.titleText ?? '',
    fixText: r.fixText ?? '',
  });

  // Bản nháp tại chỗ: người dùng bật/tắt và gõ text thoải mái rồi mới bấm Lưu, thay vì
  // mỗi thao tác là một request — sửa nhầm thì bấm Huỷ, không phải sửa ngược từng cái.
  let draft = new Map(rules.map((r) => [r.code, snapshot(r)]));
  let formKey = 'onfarm';
  // Hàng nào đang mở ô sửa text. Giữ ngoài draft vì đây là trạng thái giao diện thuần,
  // và renderList() vẽ lại toàn bộ danh sách sau mỗi lần bật/tắt.
  const expanded = new Set();

  container.innerHTML = `
    <div class="page-head">
      <div>
        <h1>Mục đối chiếu</h1>
        <p>Các luật kiểm tra số liệu bất thường, hiện trong bảng cảnh báo của phiếu.
           Chúng <b>không chặn nộp phiếu</b> — người duyệt xem lại khi duyệt.
           ${readonly ? 'Bạn chỉ có quyền xem.' : 'Tắt luật hoặc sửa nội dung cảnh báo đều có hiệu lực ngay.'}</p>
      </div>
      <div class="page-head-actions">
        <select id="arFormKey" class="filters-select" aria-label="Loại phiếu">
          <option value="onfarm">Phỏng vấn nông hộ</option>
          <option value="removal">Đo đạc trên vườn</option>
        </select>
        ${readonly ? '' : `
          <button class="btn btn-text" id="arResetBtn">Huỷ thay đổi</button>
          <button class="btn btn-tonal" id="arSaveBtn"><span class="ms">save</span>Lưu thay đổi</button>`}
      </div>
    </div>

    <div class="card ar-toolbar">
      <div class="ar-stat" id="arStat"></div>
      ${readonly ? '' : `
        <div class="ar-bulk">
          <button class="btn btn-text sm" id="arAllOn">Bật tất cả</button>
          <button class="btn btn-text sm" id="arAllOff">Tắt tất cả</button>
        </div>`}
    </div>

    <div id="arList"></div>`;

  const listHost = container.querySelector('#arList');
  const statHost = container.querySelector('#arStat');
  const saveBtn = container.querySelector('#arSaveBtn');
  const resetBtn = container.querySelector('#arResetBtn');

  const visible = () => rules.filter((r) => r.formKey === formKey);
  const changedOf = (r) => {
    const d = draft.get(r.code);
    const o = snapshot(r);
    return d.enabled !== o.enabled || d.titleText !== o.titleText || d.fixText !== o.fixText;
  };
  const isDirty = () => rules.some(changedOf);
  const hasCustomText = (code) => {
    const d = draft.get(code);
    return !!(d.titleText.trim() || d.fixText.trim());
  };

  function renderStat() {
    const rows = visible();
    const on = rows.filter((r) => draft.get(r.code).enabled).length;
    const custom = rows.filter((r) => hasCustomText(r.code)).length;
    const changed = rows.filter(changedOf).length;
    statHost.innerHTML = `<span class="ms">rule</span>
      <span><b>${on}/${rows.length}</b> luật đang bật cho ${esc(FORM_LABEL[formKey])}</span>
      ${custom ? `<span class="ar-custom-stat"><span class="ms">edit_note</span>${custom} luật sửa nội dung</span>` : ''}
      ${changed ? `<span class="ar-changed">${changed} thay đổi chưa lưu</span>` : ''}`;
    if (saveBtn) saveBtn.disabled = !isDirty();
    if (resetBtn) resetBtn.hidden = !isDirty();
  }

  /** Ô nhập không còn bọc trong `<label>` (nút "Chèn" bên trong sẽ kích hoạt cả label),
   *  nên phải ghép nhãn với ô bằng id. */
  const idOf = (code, field) => `ar-${code}-${field}`;

  /**
   * Khối "đang dùng" đặt NGAY TRÊN ô nhập của nó.
   *
   * Không nhét text mặc định thẳng vào ô: ô trống mang nghĩa "theo hệ thống", nhờ vậy
   * bản cập nhật sau này của luật tới được mọi đơn vị chưa tự đặt text. Đổ sẵn vào ô là
   * biến cả 59 luật thành "đã tuỳ biến" ngay lần lưu đầu và đóng băng câu chữ hôm nay.
   *
   * @param {string} nhan - tên phần, đọc lên cho nút "Chèn" có ngữ cảnh với screen reader
   * @param {string[]} bien - các biến thể text mặc định của mã luật này
   * @param {string} field - `titleText` | `fixText`
   */
  function defaultBox(r, nhan, bien, field) {
    if (!bien.length) return '';
    const nhieu = bien.length > 1;
    const nutChen = (i) =>
      readonly
        ? ''
        : `<button type="button" class="btn btn-text sm ar-default-copy"
             data-copy="${esc(r.code)}" data-field="${esc(field)}" data-variant="${i}"
             aria-label="Chép ${esc(nhan)}${nhieu ? ` biến thể ${i + 1}` : ''} của ${esc(r.code)} xuống ô để sửa">
             <span class="ms">content_copy</span>Chèn để sửa</button>`;

    return `
      <div class="ar-default">
        <div class="ar-default-head">
          <span class="ms ms-sm">auto_awesome</span>
          <span>Đang dùng${nhieu ? ` · ${bien.length} biến thể` : ''}</span>
          ${nhieu ? '' : nutChen(0)}
        </div>
        ${bien
          .map(
            (t, i) => `<p class="ar-default-text">
              ${nhieu ? `<b class="ar-default-n">${i + 1}.</b>` : ''}
              <span class="ar-default-body">${marked(t)}</span>
              ${nhieu ? nutChen(i) : ''}</p>`,
          )
          .join('')}
        ${
          nhieu
            ? `<p class="ar-default-note">Một mã sinh nhiều cảnh báo khác nhau, nhưng text tự đặt
                 <b>đè lên tất cả</b>. Muốn giữ khác biệt giữa chúng thì để trống ô dưới,
                 hoặc dùng <code>{title}</code> rồi viết thêm quanh nó.</p>`
            : ''
        }
      </div>`;
  }

  function textEditor(r) {
    const d = draft.get(r.code);
    const def = AUDIT_RULE_DEFAULT_TEXT[r.code] || { titles: [], fixes: [] };
    return `
      <div class="ar-text-edit">
        <p class="ar-text-help">
          Để trống = dùng nội dung hệ thống tự sinh (hiện ngay dưới đây). Chèn được:
          ${PLACEHOLDER_HINT.map(([tag, what]) => `<code>${esc(tag)}</code> ${esc(what)}`).join(' · ')}.
          <span class="ar-text-help-mark">Dấu <b>${esc(DYNAMIC_MARK)}</b> là chỗ hệ thống tự điền số liệu của phiếu —
          giữ lại bằng <code>{title}</code>.</span>
        </p>
        <div class="ar-field">
          <label for="${esc(idOf(r.code, 'titleText'))}">Tiêu đề cảnh báo</label>
          ${defaultBox(r, 'tiêu đề', def.titles, 'titleText')}
          <textarea rows="2" id="${esc(idOf(r.code, 'titleText'))}"
            data-text="titleText" data-code="${esc(r.code)}"
            placeholder="Để trống = dùng nội dung ở trên"
            ${readonly ? 'disabled' : ''}>${esc(d.titleText)}</textarea>
        </div>
        <div class="ar-field">
          <label for="${esc(idOf(r.code, 'fixText'))}">Nên làm</label>
          ${defaultBox(r, 'nên làm', def.fixes, 'fixText')}
          <textarea rows="3" id="${esc(idOf(r.code, 'fixText'))}"
            data-text="fixText" data-code="${esc(r.code)}"
            placeholder="Để trống = dùng nội dung ở trên"
            ${readonly ? 'disabled' : ''}>${esc(d.fixText)}</textarea>
        </div>
      </div>`;
  }

  function renderList() {
    const rows = visible();
    const groups = [];
    rows.forEach((r) => {
      const last = groups[groups.length - 1];
      if (last && last.name === r.group) last.items.push(r);
      else groups.push({ name: r.group, items: [r] });
    });

    listHost.innerHTML = groups
      .map(
        (g) => `
        <section class="card ar-group">
          <h2 class="ar-group-head">${esc(g.name)}</h2>
          <ul class="ar-items">
            ${g.items
              .map((r) => {
                const on = draft.get(r.code).enabled;
                const changed = changedOf(r);
                const open = expanded.has(r.code);
                return `<li class="ar-item${on ? '' : ' off'}${changed ? ' changed' : ''}">
                  <div class="ar-item-main">
                    <label class="ar-switch">
                      <input type="checkbox" data-code="${esc(r.code)}" ${on ? 'checked' : ''} ${readonly ? 'disabled' : ''}>
                      <span class="ar-switch-track"><span class="ar-switch-thumb"></span></span>
                    </label>
                    <div class="ar-item-body">
                      <span class="ar-code">${esc(r.code)}</span>
                      <span class="ar-label">${esc(r.label)}</span>
                    </div>
                    ${hasCustomText(r.code) ? '<span class="ar-custom-badge" title="Đang dùng nội dung tự đặt"><span class="ms">edit_note</span>Đã sửa nội dung</span>' : ''}
                    <span class="ar-state">${on ? 'Đang bật' : 'Đã tắt'}</span>
                    <button type="button" class="btn btn-text sm ar-text-toggle" data-toggle="${esc(r.code)}"
                      aria-expanded="${open ? 'true' : 'false'}">
                      <span class="ms">${open ? 'expand_less' : 'edit_note'}</span>${open ? 'Thu gọn' : 'Sửa nội dung'}
                    </button>
                  </div>
                  ${open ? textEditor(r) : ''}
                </li>`;
              })
              .join('')}
          </ul>
        </section>`,
      )
      .join('');

    listHost.querySelectorAll('input[data-code]').forEach((input) => {
      input.addEventListener('change', () => {
        draft.get(input.dataset.code).enabled = input.checked;
        renderList();
        renderStat();
      });
    });

    listHost.querySelectorAll('[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const code = btn.dataset.toggle;
        if (expanded.has(code)) expanded.delete(code);
        else expanded.add(code);
        renderList();
      });
    });

    // "Chèn để sửa": chép text mặc định xuống ô. Cùng lý do với handler gõ phím bên
    // dưới — không renderList(), vì vẽ lại cả danh sách sẽ cuộn mất chỗ đang làm.
    listHost.querySelectorAll('.ar-default-copy').forEach((btn) => {
      btn.addEventListener('click', () => {
        const { copy: code, field, variant } = btn.dataset;
        const def = AUDIT_RULE_DEFAULT_TEXT[code];
        const text = (field === 'titleText' ? def?.titles : def?.fixes)?.[Number(variant)];
        if (text === undefined) return;
        const area = listHost.querySelector(`textarea[data-text="${field}"][data-code="${CSS.escape(code)}"]`);
        if (!area) return;
        // Đã gõ dở thì hỏi lại: nút này nằm ngay cạnh ô, bấm nhầm rất dễ.
        if (area.value.trim() && area.value !== text
          && !window.confirm('Ô này đang có nội dung bạn tự nhập. Thay bằng nội dung mặc định?')) return;
        area.value = text;
        draft.get(code)[field] = text;
        area.closest('.ar-item')?.classList.toggle('changed', changedOf(rules.find((r) => r.code === code)));
        area.focus();
        // Con trỏ về cuối để gõ tiếp được ngay, thay vì nằm ở đầu chuỗi vừa chèn.
        area.setSelectionRange(area.value.length, area.value.length);
        renderStat();
      });
    });

    // Gõ text KHÔNG vẽ lại danh sách: renderList() thay cả DOM nên con trỏ sẽ nhảy ra
    // khỏi ô ngay ký tự đầu tiên. Chỉ cập nhật draft rồi sửa tại chỗ phần hiển thị.
    listHost.querySelectorAll('textarea[data-text]').forEach((area) => {
      area.addEventListener('input', () => {
        const rule = rules.find((r) => r.code === area.dataset.code);
        draft.get(area.dataset.code)[area.dataset.text] = area.value;
        area.closest('.ar-item')?.classList.toggle('changed', changedOf(rule));
        renderStat();
      });
    });
  }

  function renderAll() {
    renderList();
    renderStat();
  }

  container.querySelector('#arFormKey').addEventListener('change', (e) => {
    formKey = e.target.value;
    renderAll();
  });

  container.querySelector('#arAllOn')?.addEventListener('click', () => {
    visible().forEach((r) => { draft.get(r.code).enabled = true; });
    renderAll();
  });
  container.querySelector('#arAllOff')?.addEventListener('click', () => {
    // Tắt hết nghĩa là bỏ luôn lớp đối chiếu của loại phiếu này — hỏi lại một câu,
    // vì hậu quả không hiện ra ngay mà chỉ thấy khi số liệu lỗi đã vào kho.
    if (!window.confirm(`Tắt toàn bộ ${visible().length} luật đối chiếu của "${FORM_LABEL[formKey]}"? Phiếu sẽ không còn cảnh báo số liệu bất thường nào.`)) return;
    visible().forEach((r) => { draft.get(r.code).enabled = false; });
    renderAll();
  });

  resetBtn?.addEventListener('click', () => {
    draft = new Map(rules.map((r) => [r.code, snapshot(r)]));
    renderAll();
  });

  saveBtn?.addEventListener('click', async () => {
    // Gửi cả ba trường cho mỗi luật có thay đổi: chuỗi rỗng là tín hiệu "xoá text tuỳ
    // chỉnh, quay về mặc định", nên không lọc bớt trường rỗng ở đây.
    const dirty = rules.filter(changedOf);
    const changes = dirty.map((r) => {
      const d = draft.get(r.code);
      return { code: r.code, enabled: d.enabled, titleText: d.titleText, fixText: d.fixText };
    });
    if (!changes.length) return;
    // Đếm luật VỪA bị tắt, không phải luật đang tắt: sửa text của một luật vốn đã tắt
    // cũng nằm trong `changes`, báo "1 luật được tắt" ở đó là sai sự thật.
    const off = dirty.filter((r) => r.enabled && !draft.get(r.code).enabled).length;
    saveBtn.disabled = true;
    try {
      rules = await api.auditRules.update(changes);
      draft = new Map(rules.map((r) => [r.code, snapshot(r)]));
      toast(`Đã lưu ${changes.length} thay đổi${off ? ` (${off} luật được tắt)` : ''}`);
    } catch (err) {
      toast('Không lưu được: ' + err.message);
    } finally {
      renderAll();
    }
  });

  renderAll();
}
